import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canReview, canManageOps } from "@/lib/auth";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ListChecks, Filter, Calendar, Repeat } from "lucide-react";

const STATUS_COLUMNS = [
  { id: "pending", label: "Pending", color: "hsl(220 10% 60%)" },
  { id: "assigned", label: "Assigned", color: "hsl(227 100% 33%)" },
  { id: "in_progress", label: "In Progress", color: "hsl(38 92% 50%)" },
  { id: "completed", label: "Completed", color: "hsl(160 60% 45%)" },
  { id: "verified", label: "Verified", color: "hsl(158 84% 39%)" },
  { id: "overdue", label: "Overdue", color: "hsl(0 78% 55%)" },
  { id: "rejected", label: "Rejected", color: "hsl(0 78% 45%)" },
];

const PRIORITY_COLORS = {
  low: "text-muted-foreground",
  normal: "text-foreground",
  high: "text-[hsl(var(--warning))]",
  critical: "text-destructive font-bold",
};

function TaskCard({ t, areaMap, userMap }) {
  const dueDate = new Date(t.due_at);
  const isOverdue = t.status === "overdue" || (["pending","assigned","in_progress"].includes(t.status) && dueDate < new Date());
  return (
    <Link
      to={`/app/tasks/${t.id}`}
      className="block border border-border bg-card p-3 hover:border-primary hover:-translate-y-0.5 transition-all duration-150"
      data-testid={`task-card-${t.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-sm font-medium leading-snug line-clamp-2">{t.title}</div>
        {t.priority !== "normal" && (
          <span className={`text-[0.6rem] uppercase tracking-wider ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
        )}
      </div>
      {t.area_id && areaMap?.[t.area_id] && (
        <div className="text-xs text-muted-foreground mb-2 truncate">
          📍 {areaMap[t.area_id].name}
        </div>
      )}
      <div className="flex items-center justify-between text-xs">
        <span className={`flex items-center gap-1 ${isOverdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
          <Calendar className="w-3 h-3" />
          {dueDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })} {dueDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        </span>
        {t.assigned_to && userMap?.[t.assigned_to] && (
          <span className="text-muted-foreground truncate max-w-[6rem]">{userMap[t.assigned_to].name}</span>
        )}
      </div>
    </Link>
  );
}

function CreateTaskDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: templates } = useQuery({ queryKey: ["checklists"], queryFn: async () => (await api.get("/checklists")).data });
  const { data: areas } = useQuery({ queryKey: ["areas-all"], queryFn: async () => (await api.get("/facilities/areas/all")).data });
  const { data: users } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });

  const [form, setForm] = useState({
    title: "", template_id: "", area_id: "", assigned_to: "", supervisor_id: "",
    priority: "normal", due_at: new Date(Date.now() + 3600 * 1000).toISOString().slice(0, 16),
    recurrence: { type: "none", interval: 1 },
  });

  const submit = async () => {
    if (!form.title || !form.template_id) return toast.error("Title and template are required");
    try {
      const payload = {
        ...form,
        due_at: new Date(form.due_at).toISOString(),
        area_id: form.area_id || null,
        assigned_to: form.assigned_to || null,
        supervisor_id: form.supervisor_id || null,
      };
      await api.post("/tasks", payload);
      toast.success("Task created");
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["recurring"] });
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const supervisors = (users || []).filter((u) => ["supervisor", "company_admin"].includes(u.role));
  const workers = (users || []).filter((u) => u.employment_status === "active");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create task</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" placeholder="Morning bathroom clean — Room 201" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Checklist template</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger data-testid="task-template"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {templates?.filter((t) => t.is_active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["low","normal","high","critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Area</Label>
              <Select value={form.area_id} onValueChange={(v) => setForm({ ...form, area_id: v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {areas?.map((a) => <SelectItem key={a.id} value={a.id}>{a.facility_name} · {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Due at</Label>
              <Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="task-due" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Assign to</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {workers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Supervisor</Label>
              <Select value={form.supervisor_id} onValueChange={(v) => setForm({ ...form, supervisor_id: v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {supervisors.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_100px] gap-4">
            <div><Label>Recurrence</Label>
              <Select value={form.recurrence.type} onValueChange={(v) => setForm({ ...form, recurrence: { ...form.recurrence, type: v } })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["none","hourly","daily","weekly","monthly"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Every</Label>
              <Input type="number" min="1" value={form.recurrence.interval} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, interval: Number(e.target.value) } })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="task-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { user } = useAuth();
  const canCreate = canReview(user) || canManageOps(user);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("kanban");

  const { data: tasks } = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => (await api.get("/tasks")).data,
    refetchInterval: 15000,
  });
  const { data: recurring } = useQuery({
    queryKey: ["recurring"],
    queryFn: async () => (await api.get("/tasks/recurring")).data,
    enabled: canCreate,
  });
  const { data: areas } = useQuery({ queryKey: ["areas-all"], queryFn: async () => (await api.get("/facilities/areas/all")).data });
  const { data: users } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });

  const areaMap = useMemo(() => Object.fromEntries((areas || []).map((a) => [a.id, a])), [areas]);
  const userMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const filtered = (tasks || []).filter((t) => statusFilter === "all" || t.status === statusFilter);
  const byStatus = STATUS_COLUMNS.reduce((acc, c) => {
    acc[c.id] = filtered.filter((t) => t.status === c.id);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8 max-w-[1800px]">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Operations</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-2">All tasks across your workspace. Drag through statuses.</p>
        </div>
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="new-task-btn"><Plus className="w-4 h-4 mr-1" />New Task</Button>}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <TabsList>
            <TabsTrigger value="kanban" data-testid="view-kanban">Kanban</TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
            {canCreate && <TabsTrigger value="recurring" data-testid="view-recurring">Recurring</TabsTrigger>}
          </TabsList>
          {view !== "recurring" && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_COLUMNS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <TabsContent value="kanban">
          <div className="grid grid-flow-col auto-cols-[280px] gap-3 overflow-x-auto pb-4">
            {STATUS_COLUMNS.map((c) => (
              <div key={c.id} className="border border-border bg-muted/20 rounded-2xl flex flex-col min-h-[400px] overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                    <span className="text-sm font-semibold">{c.label}</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{byStatus[c.id]?.length || 0}</span>
                </div>
                <div className="flex-1 p-2 space-y-2">
                  {byStatus[c.id]?.map((t) => <TaskCard key={t.id} t={t} areaMap={areaMap} userMap={userMap} />)}
                  {byStatus[c.id]?.length === 0 && <div className="text-xs text-muted-foreground text-center py-8">Empty</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="list">
          <div className="border border-border bg-card rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 label-caps text-muted-foreground">Task</th>
                  <th className="text-left px-4 py-3 label-caps text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 label-caps text-muted-foreground">Assignee</th>
                  <th className="text-left px-4 py-3 label-caps text-muted-foreground">Due</th>
                  <th className="text-left px-4 py-3 label-caps text-muted-foreground">Priority</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                    <td className="px-4 py-3">
                      <Link to={`/app/tasks/${t.id}`} className="font-medium hover:text-primary" data-testid={`task-link-${t.id}`}>{t.title}</Link>
                      <div className="text-xs text-muted-foreground">{areaMap[t.area_id]?.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="capitalize">{t.status.replace("_"," ")}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs">{userMap[t.assigned_to]?.name || "—"}</td>
                    <td className="px-4 py-3 text-xs">{new Date(t.due_at).toLocaleString()}</td>
                    <td className={`px-4 py-3 text-xs capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-12 text-center text-muted-foreground">No tasks match your filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recurring">
          <div className="grid md:grid-cols-2 gap-3">
            {(recurring || []).map((r) => (
              <div key={r.id} className="border border-border bg-card rounded-2xl p-4 flex items-start gap-3 hover:shadow-md transition-shadow">
                <Repeat className="w-4 h-4 text-primary mt-1 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Every {r.recurrence.interval} {r.recurrence.type} · starts {new Date(r.due_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Assignee: <span className="text-foreground">{userMap[r.assigned_to]?.name || "—"}</span>
                    {" · "}Area: {areaMap[r.area_id]?.name || "—"}
                  </div>
                </div>
              </div>
            ))}
            {(!recurring || recurring.length === 0) && (
              <div className="col-span-full border border-dashed p-12 text-center text-muted-foreground">
                No recurring schedules. Create a task with a recurrence to schedule it.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <CreateTaskDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
